// =============================================================================
// player-store.ts — Player state management (Zustand)
// Project: Texas Hold'em Social Casino
// =============================================================================
// Manages player authentication, profile data, and chip balance.
// Provides login/register/logout actions and chip balance tracking.

import { create } from 'zustand'
import { PlayerProfile } from '../types'
import { useUIStore } from './ui-store'

interface PlayerState {
  // State
  profile: PlayerProfile | null
  token: string | null
  isLoading: boolean
  error: string | null

  // Login bonus
  pendingBonus: { bonus: number; streak: number; chips: number; missions: { id: string; title: string; target: number; reward: number }[] } | null

  // Actions
  loginGuest: (name?: string) => Promise<void>
  loginOAuth: (provider: 'google' | 'facebook' | 'line', token: string) => Promise<void>
  restoreToken: (token: string) => Promise<void>
  claimLoginBonus: () => Promise<void>
  dismissBonus: () => void
  logout: () => void
  updateChips: (delta: number) => void
  setChips: (amount: number) => void
  refreshProfile: () => Promise<void>
}

const API_BASE = import.meta.env.VITE_API_URL || '/api'

export const usePlayerStore = create<PlayerState>((set, get) => ({
  profile: null,
  token: null,
  isLoading: false,
  error: null,
  pendingBonus: null,

  loginGuest: async (name?: string) => {
    set({ isLoading: true, error: null })
    try {
      const url = `${API_BASE}/auth/guest`
      console.log('[Auth] POST', url)
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Login failed')

      set({
        profile: json.data.profile,
        token: json.data.token,
        isLoading: false,
      })

      localStorage.setItem('poker_token', json.data.token)
      console.log('[Auth] Logged in:', json.data.profile?.name)
      get().claimLoginBonus()
    } catch (err) {
      console.error('[Auth] Login error:', err)
      set({ isLoading: false, error: (err as Error).message })
    }
  },

  loginOAuth: async (provider, oauthToken) => {
    set({ isLoading: true, error: null })
    try {
      const res = await fetch(`${API_BASE}/auth/oauth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, token: oauthToken }),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error)

      set({
        profile: json.data.profile,
        token: json.data.token,
        isLoading: false,
      })

      localStorage.setItem('poker_token', json.data.token)
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message })
    }
  },

  claimLoginBonus: async () => {
    const token = get().token
    if (!token || get().pendingBonus) return
    try {
      const res = await fetch(`${API_BASE}/player/login-bonus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      })
      const json = await res.json()
      if (json.success && json.data.claimed) {
        set({
          pendingBonus: { bonus: json.data.bonus, streak: json.data.streak, chips: json.data.chips, missions: json.data.missions },
          profile: get().profile ? { ...get().profile!, chips: json.data.chips } : null,
        })
      }
    } catch (err) {
      console.error('[Bonus] Failed to claim:', err)
    }
  },

  dismissBonus: () => {
    set({ pendingBonus: null })
  },

  logout: () => {
    localStorage.removeItem('poker_token')
    set({ profile: null, token: null })
    useUIStore.getState().navigateTo('login')
  },

  updateChips: (delta: number) => {
    const profile = get().profile
    if (profile) {
      set({ profile: { ...profile, chips: Math.max(0, profile.chips + delta) } })
    }
  },

  setChips: (amount: number) => {
    const profile = get().profile
    if (profile) {
      set({ profile: { ...profile, chips: amount } })
    }
  },

  refreshProfile: async () => {
    const token = get().token
    if (!token) return

    try {
      const res = await fetch(`${API_BASE}/player/${token}`)
      const json = await res.json()
      if (json.success && json.data) {
        set({ profile: json.data })
      }
    } catch (err) {
      console.error('Failed to refresh profile:', err)
    }
  },
}))
